<script setup lang="ts">
import {
  WidgetHeroBannerLayout,
  WidgetHeroBannerIllustration,
  type IHeroBanner
} from '~/components/widget/hero-banner'

const props = defineProps<
  IHeroBanner & {
    variables?: Record<string, string | number>
    // Lets a page show the intro only on some screen sizes without branching SSR output
    introClass?: string
  }
>()

const route = useRoute()

// Get data from unified cache system
const { data: cachedData } = await useAppCache()

const seoData = computed(() => {
  let currentPath = route.path.startsWith('/') ? route.path.substring(1) : route.path

  if (currentPath.startsWith('ky/')) {
    currentPath = currentPath.substring(3)
  }

  const seoEntries = cachedData.value?.data?.data || {}

  if (seoEntries[currentPath]) return seoEntries[currentPath]

  for (const key in seoEntries) {
    if (key.includes('(')) {
      const regex = new RegExp(`^${key}$`)
      if (regex.test(currentPath)) {
        return seoEntries[key]
      }
    }
  }

  return null
})

const replaceVariables = (text: string | null) => {
  if (!text) return ''
  return text.replace(/:([a-zA-Z0-9_.]+):/g, (_, key) => {
    return props.variables?.[key] !== undefined ? String(props.variables[key]) : ''
  })
}

const heading = computed(() => {
  let text = ''
  if (seoData.value && seoData.value.type === 'seo_settings' && seoData.value.title) {
    text = seoData.value.title
  } else {
    text = props.heading || ''
  }
  return replaceVariables(text)
})

const description = computed(() => {
  let text = ''
  if (seoData.value && seoData.value.type === 'seo_settings' && seoData.value.upper_description) {
    text = seoData.value.upper_description
  } else {
    text = props.text || ''
  }
  return replaceVariables(text)
})
</script>

<template>
  <WidgetHeroBannerLayout :backdrop="props.backdrop">
    <div v-if="props.intro" class="md:text-lg" :class="props.introClass">{{ props.intro }}</div>
    <Heading v-if="heading" tag="h1">{{ heading }}</Heading>
    <div
      v-if="description"
      v-hydrated-html="description"
      class="max-w-2xl space-y-3 leading-6 text-gray-700 md:leading-7 [&_b]:font-semibold [&_b]:text-brand-gray [&_strong]:font-semibold [&_strong]:text-brand-gray"
    />
    <div
      v-if="props.actionBtn || props.additionalActionBtn"
      class="flex flex-col items-center gap-2 md:flex-row md:gap-3"
    >
      <Button v-if="props.actionBtn" as-child>
        <NuxtLinkLocale :to="props.actionBtn.url">{{ props.actionBtn.label }}</NuxtLinkLocale>
      </Button>
      <div
        v-if="props.additionalActionBtn"
        class="flex flex-col items-center gap-2 md:flex-row md:gap-3"
      >
        <span>{{ $t('common.or') }}</span>
        <Button variant="green" as-child>
          <NuxtLinkLocale :to="props.additionalActionBtn.url">{{
            props.additionalActionBtn.label
          }}</NuxtLinkLocale>
        </Button>
      </div>
    </div>

    <template v-if="props.illustration || props.image" #illustration>
      <WidgetHeroBannerIllustration v-if="props.illustration" :name="props.illustration" />
      <NuxtImg
        v-else-if="props.image"
        :src="props.image"
        :alt="heading"
        class="pointer-events-none size-[18.5rem] select-none object-contain"
        format="avif,webp"
        fetchpriority="high"
      />
    </template>
  </WidgetHeroBannerLayout>
</template>
